<script setup lang="ts">
import { CloseOutlined } from "@ant-design/icons-vue";
import BgPng from "@main/assets/images/bg.webp";
import { useAppTabsStore } from "@main/stores";
import { message, Modal } from "ant-design-vue";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, onBeforeRouteLeave } from "vue-router";
import {
	approvalAffiliatedOrganization,
	fetchAssociationApprovalList,
	type AssociationApprovalListItem,
} from "@/services/businessCenter/affiliatedOrganizations";
import SiderMenu from "./SiderMenu.vue";

const route = useRoute();
const appTabsStore = useAppTabsStore();

watch(
	() => route,
	(value) => {
		if (value.path === "/desktopCenter" && appTabsStore.activeKey) {
			appTabsStore.switchToActiveTab();
		}
	},
	{
		deep: true,
		immediate: true,
	}
);

onBeforeRouteLeave(() => {
	appTabsStore.leaveDesktop();
});

// 关联公司审核相关逻辑
const isDesktopActive = computed(() => route.path === "/desktopCenter");
const isClose = ref(false);
const pendingReviewRelatedApplications = ref<AssociationApprovalListItem[]>([]);

const getPendingReviewRelatedApplications = async () => {
	const res = await fetchAssociationApprovalList({ status: 0 });

	pendingReviewRelatedApplications.value = res.data;
};

onMounted(() => {
	getPendingReviewRelatedApplications();
});

const handleClose = () => {
	isClose.value = true;
};

const handleConfirm = (item: AssociationApprovalListItem) => {
	Modal.confirm({
		title: `加入${item.parent_company_name}`,
		type: "warning",
		content: `确定要同意加入${item.parent_company_name}吗？加入后您将成为${item.parent_company_name}的下级组织成员，ta可以查看您组织的部分信息，如组织架构等。`,
		onOk: async () => {
			await approvalAffiliatedOrganization({
				id: item.id,
				status: 1,
				refuse_remark: "",
			});

			message.success(`已同意加入${item.parent_company_name}`);
			getPendingReviewRelatedApplications();
		},
	});
};

const rejectModelOpen = ref(false);
const formState = ref({
	id: 0,
	status: 2,
	refuse_remark: "",
});
const rejectLoading = ref(false);

const handleReject = (item: AssociationApprovalListItem) => {
	formState.value.id = item.id;
	rejectModelOpen.value = true;
};

const handleRejectConfirm = async () => {
	try {
		rejectLoading.value = true;

		await approvalAffiliatedOrganization(formState.value);

		message.success("已拒绝加入");
		isClose.value = true;
		getPendingReviewRelatedApplications();
	} catch (_error) {
		//
	} finally {
		rejectLoading.value = false;
	}
};
</script>

<template>
	<div class="full relative overflow-hidden">
		<img class="full object-cover object-center" :src="BgPng" alt="" />

		<div class="full absolute top-0 left-0 z-1 flex">
			<SiderMenu />

			<div class="relative min-h-0 flex-1">
				<KeepAliveRouterView />

				<!-- 待审核关联公司提示 -->
				<div
					v-if="pendingReviewRelatedApplications.length > 0 && !isClose"
					class="bottom-xh fixed left-1/2 z-50 w-[500px] -translate-x-1/2"
				>
					<div
						class="pt-ms pb-mm px-mm rounded-ml association-approval"
						:class="[isDesktopActive ? 'text-text-primary bg-white/40' : 'bg-black/50 text-white']"
					>
						<div
							v-for="(item, index) in pendingReviewRelatedApplications"
							:key="index"
							class="w-full"
						>
							<div class="mb-mms flex items-center justify-between">
								<div class="flex-1 text-lg font-extrabold">
									{{ item.parent_company_name }}邀请您加入组织
								</div>

								<CloseOutlined
									class="cursor-pointer !text-lg !font-extrabold"
									@click="handleClose"
								/>
							</div>

							<div class="mb-smm text-base font-medium">{{ item.remark }}</div>

							<div class="flex justify-end">
								<a-space :size="10">
									<a-button type="primary" @click="handleConfirm(item)">同意加入</a-button>
									<a-button
										:type="isDesktopActive ? 'primary' : 'default'"
										ghost
										@click="handleReject(item)"
									>
										拒绝加入
									</a-button>
								</a-space>
							</div>
						</div>
					</div>

					<div
						class="association-approval-border rounded-ml pointer-events-none absolute inset-0"
					></div>
				</div>
			</div>
		</div>

		<custom-modal
			v-model:open="rejectModelOpen"
			title="拒绝加入"
			:confirm-loading="rejectLoading"
			@ok="handleRejectConfirm"
		>
			<a-form layout="vertical">
				<a-form-item label="拒绝原因" name="refuse_remark">
					<a-textarea
						v-model:value="formState.refuse_remark"
						placeholder="请输入拒绝原因"
						:rows="3"
						show-count
						:maxlength="140"
					/>
				</a-form-item>
			</a-form>
		</custom-modal>
	</div>
</template>

<style scoped lang="scss">
.association-approval {
	backdrop-filter: blur(32px) saturate(160%);
	-webkit-backdrop-filter: blur(32px) saturate(160%);
}

.association-approval-border {
	background: linear-gradient(89deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.6));
	padding: 1px;
	mask:
		linear-gradient(#fff 0 0) content-box,
		linear-gradient(#fff 0 0);
	mask-composite: exclude;
	-webkit-mask:
		linear-gradient(#fff 0 0) content-box,
		linear-gradient(#fff 0 0);
	-webkit-mask-composite: xor;
}
</style>
